import {FlowAvalancheState} from '@fantasmagames/slot/src/modules/flow/states/FlowAvalancheState';
import {AvalancheReelsEvents} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReelsEvents';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {FlowModule} from '@fantasmagames/slot/src/modules/flow/FlowModule';
import {BigWinEvents} from '@fantasmagames/slot/src/modules/bigwin/BigWinEvents';
import {GameRecodeLogic} from 'src/logic/recode/GameRecodeLogic';
import {EventsFlowPairs} from '@fantasmagames/slot/src/modules/flow/states/QueueEventsHandler';
import {GameFlowEvents} from 'src/flow/GameFlowEvents';
import {GameAvalancheReelsEvents} from 'src/avalanche/GameAvalancheReelsEvents';
import {WinPresentationEvents} from '@fantasmagames/slot/src/modules/winpresentation/WinPresentationEvents';
import {GameWinUtils} from 'src/logic/GameWinUtils';
import {GameWinPresentationEvents} from 'src/winpresentation/GameWinPresentationEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';
import {FreeSpinsTriggerModule} from 'src/freespinstrigger/FreeSpinsTriggerModule';

export class GameFlowAvalancheState extends FlowAvalancheState {

	protected setupEvents(): void {
		super.setupEvents();
		this.on(AvalancheReelsEvents.REMOVING_WIN_FINISHED, this.onRemovingWinFinished);
	}

	protected hasAnyBeforeWinFeatures(): boolean {
		const logic: GameRecodeLogic = (this.module.logic as GameRecodeLogic);

		return logic.hasWinsToHighlight();
	}

	protected addBeforeWinFeatures(): void {
		super.addBeforeWinFeatures();

		const logic: GameRecodeLogic = (this.module.logic as GameRecodeLogic);
		const eventsPairs: EventsFlowPairs[] = [];

		if (logic.hasWinsToHighlight()) {
			eventsPairs.push({
				start: GameFlowEvents.HAS_WIN_SYMBOLS_TO_HIGHLIGHT,
				end: GameAvalancheReelsEvents.WIN_HIGHLIGHTING_FINISHED,
			});
		}

		if (eventsPairs.length > 0) {
			this._queueEvents.addEventsPairFlow(eventsPairs, this.decideOnNextAction.bind(this));
		} else {
			throw new Error(`GameFlowAvalancheState::addBeforeWinFeatures need more events`);
		}
	}

	protected onRemovingWinFinished(): void {
		this.emit(FlowModuleEvents.CAN_DROP_NEW);
	}

	protected setupNextStateAvalancheFlow(): void {
		this._queueEvents.addFunctionCallingEventToWaitingList(FlowModuleEvents.AVALANCHE_WON, () => {
			this.module.gotoState(FlowModule.AVALANCHE_STATE);
		});

		this.emit(FlowModuleEvents.AVALANCHE_WON);
	}

	protected setupBigWinNoWinAfterwards(): void {
		this.emit(FlowModuleEvents.WON_BIG_WIN);
	}

	protected setupNextStateFreeSpinsFlow(): void {
		const logic: GameRecodeLogic = this.module.logic as GameRecodeLogic;

		if (logic.getLastAvalancheMultiplier() > 1) {
			if (GameWinUtils.isBigWinWithMultiplier(logic)) {
				this.emit(GameFlowEvents.HAS_BIGWIN_WITH_MULTIPLIER);
				this.setupWinWithBigWinFreeSpins();
				this.addCheckForExitFreeSpinAfterBigWin();
			} else {
				this.emit(GameFlowEvents.HAS_WIN_WITH_MULTIPLIER);
				this.setupWinFreeSpins();
				this.addCheckForExitFreeSpin();
			}
		} else {
			if (GameWinUtils.isBigWinNoMultiplier(logic)) {
				this.emit(GameFlowEvents.HAS_BIGWIN_WITH_NO_MULTIPLIER);
				this.setupBigWinNoMultiplier();
				this.addCheckForExitFreeSpinAfterBigWin();
			} else {
				this.emit(GameFlowEvents.HAS_WIN_WITH_NO_MULTIPLIER);
				this.setupWinNoMultiplier();
				this.addCheckForExitFreeSpin();
			}
		}
	}

	protected addCheckForExitFreeSpinAfterBigWin(): void {
		if (this.module.logic.isFreeSpinsStarted && this.module.logic.freeSpinsLeft <= 0) {
			this._queueEvents.addFunctionCallingEventToWaitingList(
				BigWinEvents.FINISHED,
				this.gotoBasicState.bind(this),
			);
		}
	}

	protected setupBigWinNoMultiplier(): void {
		const logic: GameRecodeLogic = this.module.logic as GameRecodeLogic;
		if (logic.hasFreeSpinsRetrigger) {
			this._queueEvents.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.FREESPINS_RETRIGGER_WIN);
			this._queueEvents.addEventToWaitingList(FreeSpinsEvents.FREE_SPINS_RETRIGGER_FINISHED, FlowModuleEvents.FREESPINS_WIN);
		} else if (logic.isFreeSpinsTrigger()) {
			this._queueEvents.addEventToWaitingList(BigWinEvents.FINISHED, GameFlowEvents.PLAY_FREE_SPINS_TRIGGER);
			this._queueEvents.addEventToWaitingList(FreeSpinsTriggerModule.FREE_SPINS_TRIGGER_FINISHED, FlowModuleEvents.FREESPINS_WIN);
		} else {
			this._queueEvents.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.FREESPINS_WIN);
		}
		this.setupBigWinNoIndividualNoSpin();
		this.setupCantSpinBigWin();
	}

	protected setupWinNoMultiplier(): void {
		const logic: GameRecodeLogic = this.module.logic as GameRecodeLogic;
		if (logic.hasFreeSpinsRetrigger) {
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.FREESPINS_RETRIGGER_WIN);
			this._queueEvents.addEventToWaitingList(FreeSpinsEvents.FREE_SPINS_RETRIGGER_FINISHED, FlowModuleEvents.FREESPINS_WIN);
		} else if (logic.isFreeSpinsTrigger()) {
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, GameFlowEvents.PLAY_FREE_SPINS_TRIGGER);
			this._queueEvents.addEventToWaitingList(FreeSpinsTriggerModule.FREE_SPINS_TRIGGER_FINISHED, FlowModuleEvents.FREESPINS_WIN);
		} else {
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.FREESPINS_WIN);
		}
		this.setupCantSpinWin();
	}

	protected setupNextStateSpinFlow(): void {
		const logic: GameRecodeLogic = this.module.logic as GameRecodeLogic;
		if (logic.getLastAvalancheMultiplier() > 1) {
			if (GameWinUtils.isBigWinWithMultiplier(logic)) {
				this.emit(GameFlowEvents.HAS_BIGWIN_WITH_MULTIPLIER);
				this.setupWinWithBigWin();
				this.addCheckForExitFreeSpinAfterBigWin();
			} else {
				this.emit(GameFlowEvents.HAS_WIN_WITH_MULTIPLIER);
				this.setupWin();
				this.addCheckForExitFreeSpin();
			}
		} else {
			if (GameWinUtils.isBigWinNoMultiplier(logic)) {
				this.emit(GameFlowEvents.HAS_BIGWIN_WITH_NO_MULTIPLIER);
				this.setupBigWin();
				this.addCheckForExitFreeSpinAfterBigWin();
			} else {
				this.emit(GameFlowEvents.HAS_WIN_WITH_NO_MULTIPLIER);
				this.setupWin();
				this.addCheckForExitFreeSpin();
			}
		}
	}

	protected setupBigWin(): void {
		this._queueEvents.addFunctionCallingEventToWaitingList(FlowModuleEvents.CAN_SPIN, () => {
			this.module.gotoState(FlowModule.BASIC_STATE);
		});
		this._queueEvents.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.CAN_SPIN);
		this.emit(FlowModuleEvents.WON_BIG_WIN);
	}

	protected setupWin(): void {
		this._queueEvents.addFunctionCallingEventToWaitingList(FlowModuleEvents.CAN_SPIN, () => {
			this.module.gotoState(FlowModule.BASIC_STATE);
		});
		this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.CAN_SPIN);
		this.emit(FlowModuleEvents.WON);
	}

	protected setupWinFreeSpins(): void {
		const logic: GameRecodeLogic = this.module.logic as GameRecodeLogic;
		if (logic.hasFreeSpinsRetrigger) {
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.FREESPINS_RETRIGGER_WIN);
			this._queueEvents.addEventToWaitingList(FreeSpinsEvents.FREE_SPINS_RETRIGGER_FINISHED, FlowModuleEvents.FREESPINS_WIN);
		} else if (logic.isFreeSpinsTrigger()) {
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, GameFlowEvents.PLAY_FREE_SPINS_TRIGGER);
			this._queueEvents.addEventToWaitingList(FreeSpinsTriggerModule.FREE_SPINS_TRIGGER_FINISHED, FlowModuleEvents.FREESPINS_WIN);
		} else {
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.FREESPINS_WIN);
		}
		this.emit(FlowModuleEvents.WON);
	}

	protected setupWinWithBigWin(): void {
		this._queueEvents.addFunctionCallingEventToWaitingList(GameWinPresentationEvents.PRE_MULTIPLIER_BIG_WIN_FINISHED, () => {
			this.setupBigWin();
		});
		this.emit(FlowModuleEvents.WON);
	}

	protected setupWinWithBigWinFreeSpins(): void {
		this._queueEvents.addFunctionCallingEventToWaitingList(GameWinPresentationEvents.PRE_MULTIPLIER_BIG_WIN_FINISHED, () => {
			const logic: GameRecodeLogic = this.module.logic as GameRecodeLogic;
			if (logic.hasFreeSpinsRetrigger) {
				this._queueEvents.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.FREESPINS_RETRIGGER_WIN);
				this._queueEvents.addEventToWaitingList(FreeSpinsEvents.FREE_SPINS_RETRIGGER_FINISHED, FlowModuleEvents.FREESPINS_WIN);
			} else if (logic.isFreeSpinsTrigger()) {
				this._queueEvents.addEventToWaitingList(BigWinEvents.FINISHED, GameFlowEvents.PLAY_FREE_SPINS_TRIGGER);
				this._queueEvents.addEventToWaitingList(FreeSpinsTriggerModule.FREE_SPINS_TRIGGER_FINISHED, FlowModuleEvents.FREESPINS_WIN);
			} else {
				this._queueEvents.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.FREESPINS_WIN);
			}
			this.setupBigWinNoIndividualNoSpin();
			this.setupCantSpinBigWin();

		});
		this.emit(FlowModuleEvents.WON);
	}
}



// WEBPACK FOOTER //
// ./src/flow/states/GameFlowAvalancheState.ts